/**
 * entity-search — the RECORDS half of the header search (2026-10-02).
 *
 * The header field promised "Search events, guests, locations" and only ever
 * matched page names: a Team Member typing the name of the person on their
 * 2pm appointment got "No results … Filtered to what you have access to",
 * and so did every other role. This module searches real records, each
 * through a read that already exists and only when the signed-in role holds
 * the capability that read (and the screen it opens) is gated on:
 *
 *   your calendar  getMyCalendar (GET /v1/me/calendar)  myschedule.read
 *                  — session-scoped: the member's own appointments, the
 *                  events they host and their walk-ins. Read ONCE per open
 *                  search (a fixed window around today) and matched locally,
 *                  so typing costs no requests.
 *   events         GET /v1/admin/events?q=               events.read
 *   customers      GET /v1/admin/customers?q=            customers.read
 *   locations      the list the location picker already holds (no new read)
 *
 * Nothing here invents a record or widens a scope: a role that holds none of
 * these reads gets page results only, and the placeholder says so (see
 * `searchScopeNouns`). Results open the surface that owns the record — the
 * My Calendar deep link (`?day=&entry=`, the same landing a scan uses), the
 * event drawer, the customer page, the location page.
 *
 * Workspace scope (2026-10-03): every result belongs to ONE workspace. The
 * same account can switch workspace in place, and the chrome shows the new
 * workspace's name before the switch is confirmed. So every read here, the
 * own-calendar cache and its in-flight request included, is keyed by the
 * CONFIRMED workspace plus the user (`scopeKeyOf`), never the user alone; the
 * cache, the in-flight request and the server-refusal memory are purged the
 * instant a switch starts (`onTenantSwitching`); nothing is searched while a
 * switch is pending; and an answer that arrives for an earlier scope is
 * dropped. A search during a switch shows the sources as loading, then the
 * new workspace's records once the switch is confirmed.
 */
import { useEffect, useRef, useState } from "react";
import { getMyCalendar, isApiError, listBookingCustomers } from "@v0y4ge/api-client";
import { fetchEventsList } from "../lib/api";
import { toUIEvent, type UIEvent } from "../lib/events-adapter";
import { calendarDateKey, friendlyDate, friendlyDateNoYear, friendlyTime } from "../lib/datetime";
import { t as i18nT } from "../lib/i18n";
import { moduleKnownAbsent, useTenantModules } from "../lib/tenant-modules";
import { can, getActiveTenantSlug, getConfirmedTenantSlug, getUser, onTenantChange, onTenantIdentityResolved, onTenantSwitching, useUser } from "./access";

type RemoteSource = "calendar" | "events" | "customers";

function userKeyOf(u: { id?: string; email?: string; name?: string }): string {
  return String(u.id || u.email || u.name || "-");
}
/** True between a workspace switch starting and its confirmation (or
 *  rollback): the chrome already names the target workspace, but the session
 *  is not confirmed live there, so nothing may be read or shown. */
function switchPending(): boolean {
  return getConfirmedTenantSlug() === null && getActiveTenantSlug() !== null;
}
/** The scope every read and every stored answer is keyed by: the CONFIRMED
 *  workspace plus the user. Before the workspace first resolves the slug is
 *  empty (the page's own session); that is a different key from the resolved
 *  one, so nothing read under it is reused once the slug arrives. */
function scopeKeyOf(u: { id?: string; email?: string; name?: string }): string {
  return (getConfirmedTenantSlug() ?? "") + "|" + userKeyOf(u);
}

/* Bumped on every workspace switch (start, confirmation, rollback). A read
 * that started under an earlier generation never writes the shared cache. */
let scopeGeneration = 0;
const scopeListeners = new Set<() => void>();
function scopeChanged(): void {
  scopeListeners.forEach((fn) => { try { fn(); } catch (e) { console.warn("entity-search scope listener failed:", e); } });
}

/* A read the SERVER refuses (403) is out of scope for the rest of the session,
 * exactly as if the role did not hold its capability. The client-side `can()`
 * falls back to the role catalogue when the session carries no served
 * permission list (a demo persona sign-in), so it can promise a read the
 * server then refuses. Without this, every search showed that source's
 * skeleton rows and then "… could not be searched" with a Retry that can never
 * succeed. Keyed by workspace + user, so a sign-in as someone else, or the same
 * account in another workspace, starts clean. */
let deniedSources: { key: string; sources: Set<RemoteSource> } = { key: "", sources: new Set() };
function isDenied(source: RemoteSource, key: string): boolean {
  return deniedSources.key === key && deniedSources.sources.has(source);
}
function markDenied(source: RemoteSource, key: string): void {
  if (deniedSources.key !== key) deniedSources = { key, sources: new Set() };
  deniedSources.sources.add(source);
}
const isForbidden = (e: unknown) => isApiError(e) && e.status === 403;

/** Which record sources this role may search. Each is the read's own gate,
 *  minus any read the server has refused this session. */
export interface SearchScope { calendar: boolean; events: boolean; customers: boolean; locations: boolean }
export function searchScope(): SearchScope {
  const key = scopeKeyOf(getUser() as { id?: string; email?: string; name?: string });
  return {
    calendar: can("myschedule.read") && !isDenied("calendar", key),
    events: can("events.read") && !isDenied("events", key),
    /* The customers read is the booking module's: a tenant without it answers 503. Unknown keeps the lane. */
    customers: can("customers.read") && !isDenied("customers", key) && !moduleKnownAbsent("booking"),
    locations: can("locations.read"),
  };
}

/** One record result. `open` is what Enter / click does. */
export interface EntityHit {
  key: string;
  source: "calendar" | "events" | "customers" | "locations";
  title: string;
  sub: string;
  open: { route: string; params?: Record<string, unknown> } | { event: UIEvent };
}

export type SourceState = "idle" | "loading" | "ready" | "error";
export interface EntitySearchResult {
  hits: EntityHit[];
  /** Per remote source; "idle" when the source is out of scope or the query is too short. */
  state: Record<"calendar" | "events" | "customers", SourceState>;
  /** The own-calendar read hit its 500-entry cap: matches may be incomplete. */
  calendarTruncated: boolean;
  retry: () => void;
}

/** Remote reads start at two characters; one letter matches half the directory. */
export const MIN_REMOTE_QUERY = 2;
const DEBOUNCE_MS = 250;
const PER_SOURCE = 6;
/** The own-calendar window: a month back, three months ahead. */
const CAL_BACK_DAYS = 30;
const CAL_AHEAD_DAYS = 90;
const CAL_TTL_MS = 60_000;

type CalEntry = { id: string; startsAt: string; title: string; subtitle: string; customerName: string; locationName: string; status?: string };
type CalCache = { key: string; at: number; tz: string; truncated: boolean; entries: CalEntry[] };
let calCache: CalCache | null = null;
let calInflight: { key: string; p: Promise<CalCache> } | null = null;

function dayKeyOffset(days: number): string {
  return calendarDateKey(new Date(Date.now() + days * 86_400_000));
}

/* The instant a switch starts: forget every answer and every refusal from the
 * outgoing workspace, and stop sharing its in-flight read. Nothing is fetched
 * here; the hooks read again only once the switch is confirmed. */
onTenantSwitching(() => {
  scopeGeneration += 1;
  calCache = null;
  calInflight = null;
  deniedSources = { key: "", sources: new Set() };
  scopeChanged();
});
/* Confirmation, rollback, and the first resolution of the workspace: the
 * scope key moved, so mounted searches re-render under the new key. */
onTenantChange(() => { scopeGeneration += 1; scopeChanged(); });
onTenantIdentityResolved(() => { scopeChanged(); });

function readOwnCalendar(scopeKey: string, force: boolean): Promise<CalCache> {
  if (!force && calCache && calCache.key === scopeKey && Date.now() - calCache.at < CAL_TTL_MS) return Promise.resolve(calCache);
  if (!force && calInflight && calInflight.key === scopeKey) return calInflight.p;
  const gen = scopeGeneration;
  const p = getMyCalendar({ view: "list", from: dayKeyOffset(-CAL_BACK_DAYS), to: dayKeyOffset(CAL_AHEAD_DAYS) })
    .then((r) => {
      const res = r as unknown as { events?: CalEntry[]; truncated?: boolean; timezone?: string };
      const next: CalCache = { key: scopeKey, at: Date.now(), tz: res.timezone || "", truncated: !!res.truncated, entries: Array.isArray(res.events) ? res.events : [] };
      /* A read that outlived a workspace switch never becomes the cache; its
         caller drops it too, because the key no longer matches. */
      if (gen === scopeGeneration) calCache = next;
      return next;
    })
    .finally(() => { if (calInflight && calInflight.p === p) calInflight = null; });
  calInflight = { key: scopeKey, p };
  return p;
}

const norm = (s: unknown) => String(s ?? "").toLowerCase();

/** "2:00 PM · Fri, Oct 2" — the time FIRST. The subtitle is one ellipsised
 *  line about 300px wide; with the date leading ("Fri, Oct 2, 2026 · 2:00 …")
 *  the time was the part the ellipsis cut, and the time is what tells two
 *  appointments with the same person apart. The year is left out inside the
 *  current year (the window is a month back, three ahead). */
function calendarWhen(startsAt: string, tz?: string): string {
  const time = friendlyTime(startsAt, tz);
  const sameYear = calendarDateKey(startsAt, tz).slice(0, 4) === calendarDateKey(new Date(), tz).slice(0, 4);
  const date = sameYear ? friendlyDateNoYear(startsAt, tz) : friendlyDate(startsAt, tz);
  if (!time) return date;
  return i18nT("chrome.searchPanel.hitWhen.icu", "{time} · {date}", { time, date });
}

/** Own-calendar matches, soonest-upcoming first, then the most recent past. */
function calendarHits(cache: CalCache, q: string): EntityHit[] {
  const now = Date.now();
  const tz = cache.tz || undefined;
  const matched = cache.entries.filter((e) => e.status !== "cancelled" && [e.customerName, e.title, e.subtitle, e.locationName].some((v) => norm(v).includes(q)));
  const t = (e: CalEntry) => new Date(e.startsAt).getTime();
  const upcoming = matched.filter((e) => t(e) >= now).sort((a, b) => t(a) - t(b));
  const past = matched.filter((e) => t(e) < now).sort((a, b) => t(b) - t(a));
  return upcoming.concat(past).slice(0, PER_SOURCE).map((e) => {
    const who = (e.customerName || "").trim();
    const what = (e.title || "").trim();
    return {
      key: "cal:" + e.id,
      source: "calendar" as const,
      title: who || what,
      sub: [calendarWhen(e.startsAt, tz), who ? what : (e.locationName || "").trim()].filter(Boolean).join(" · "),
      /* The VENUE day: the board's deep link finds the day first, then the
         element in that day's read (`goEntry`). */
      open: { route: "workforce.myschedule", params: { day: calendarDateKey(e.startsAt, tz), entry: e.id } },
    };
  });
}

/** Location matches over the list the location picker already loaded. */
export function locationHits(locations: { id: string; name: string; address?: string; city?: string }[], q: string): EntityHit[] {
  if (!q) return [];
  return locations
    .filter((l) => norm([l.name, l.address, l.city].filter(Boolean).join(" ")).includes(q))
    .slice(0, PER_SOURCE)
    .map((l) => ({
      key: "loc:" + l.id,
      source: "locations" as const,
      title: l.name,
      sub: [l.address, l.city].filter(Boolean).join(" · "),
      open: { route: "locations.edit", params: { id: l.id, view: 1 } },
    }));
}

/** One debounced server-side search source. A 403 reports `onForbidden` and
 *  drops the source (no skeleton, no failure line); any other failure is
 *  "error", which the panel offers to retry. Every stored answer carries the
 *  workspace + user scope it was read under (`k`) and is shown only while that
 *  is still the scope: a change of scope cancels the read in flight, and an
 *  answer that lands anyway is dropped. */
function useRemoteSource(want: boolean, k: string, gen: number, q: string, nonce: number, run: () => Promise<EntityHit[]>, onForbidden: (k: string) => void): { state: SourceState; hits: EntityHit[] } {
  const [s, setS] = useState<{ k: string; q: string; state: SourceState; hits: EntityHit[] }>({ k: "", q: "", state: "idle", hits: [] });
  const runRef = useRef(run);
  runRef.current = run;
  const forbiddenRef = useRef(onForbidden);
  forbiddenRef.current = onForbidden;
  useEffect(() => {
    if (!want) { setS({ k: "", q: "", state: "idle", hits: [] }); return; }
    let live = true;
    const current = () => live && gen === scopeGeneration;
    setS((prev) => ({ k, q, state: "loading", hits: prev.k === k && prev.q === q ? prev.hits : [] }));
    const timer = setTimeout(() => {
      runRef.current()
        .then((hits) => { if (current()) setS((prev) => (prev.k === k && prev.q === q ? { k, q, state: "ready", hits } : prev)); })
        .catch((e) => {
          if (!current()) return;
          if (isForbidden(e)) { setS({ k: "", q: "", state: "idle", hits: [] }); forbiddenRef.current(k); return; }
          setS((prev) => (prev.k === k && prev.q === q ? { k, q, state: "error", hits: [] } : prev));
        });
    }, DEBOUNCE_MS);
    return () => { live = false; clearTimeout(timer); };
  }, [k, gen, q, want, nonce]);
  if (!want) return { state: "idle", hits: [] };
  return s.k === k && s.q === q ? { state: s.state, hits: s.hits } : { state: "loading", hits: [] };
}

/**
 * The remote half: own calendar (one read, local match), events and customers
 * (debounced server-side search, one effect each so a change to one source
 * never re-issues the other's read). `active` is false while the panel is
 * closed, which cancels everything in flight.
 */
export function useEntitySearch(query: string, active: boolean): EntitySearchResult {
  const u = useUser() as { id?: string; email?: string; name?: string };
  /* Re-render on every workspace switch step: the scope key, the pending
     flag and the generation below are read from the access layer here. */
  const [, setScopeTick] = useState(0);
  useEffect(() => {
    const fn = () => setScopeTick((n) => n + 1);
    scopeListeners.add(fn);
    return () => { scopeListeners.delete(fn); };
  }, []);
  const scopeKey = scopeKeyOf(u);
  const gen = scopeGeneration;
  const pending = switchPending();
  const q = query.trim().toLowerCase();
  /* Subscribe to the tenant's module list so the customers lane follows it once it is known. */
  useTenantModules();
  const [, setDeniedTick] = useState(0);
  const scope = searchScope();
  const [nonce, setNonce] = useState(0);
  const forceCal = useRef(false);
  const [cal, setCal] = useState<{ state: SourceState; cache: CalCache | null }>({ state: "idle", cache: null });
  const deny = (source: RemoteSource, k: string) => { markDenied(source, k); setDeniedTick((n) => n + 1); };
  const searching = active && q.length >= MIN_REMOTE_QUERY;

  /* Own calendar: read when the search first has something to match. Never
     while a workspace switch is pending. */
  const wantCal = searching && !pending && scope.calendar;
  useEffect(() => {
    if (!wantCal) return;
    let live = true;
    const k = scopeKey;
    const current = () => live && gen === scopeGeneration;
    const force = forceCal.current;
    forceCal.current = false;
    setCal((prev) => (prev.cache && prev.cache.key === k && !force ? prev : { state: "loading", cache: prev.cache && prev.cache.key === k ? prev.cache : null }));
    readOwnCalendar(k, force)
      .then((cache) => { if (current() && cache.key === k) setCal({ state: "ready", cache }); })
      .catch((e) => {
        if (!current()) return;
        if (isForbidden(e)) { setCal({ state: "idle", cache: null }); deny("calendar", k); return; }
        setCal((prev) => ({ state: "error", cache: prev.cache && prev.cache.key === k ? prev.cache : null }));
      });
    return () => { live = false; };
  }, [wantCal, scopeKey, gen, nonce]);

  /* Events + customers: debounced, dropped on the next keystroke and on any
     change of workspace. */
  const wantEvents = searching && !pending && scope.events;
  const wantCustomers = searching && !pending && scope.customers;
  const events = useRemoteSource(wantEvents, scopeKey, gen, q, nonce, () => fetchEventsList({ q: query.trim(), limit: PER_SOURCE }).then((r) => {
    const rows = ((r as unknown as { data?: unknown[]; events?: unknown[] }).data ?? (r as unknown as { events?: unknown[] }).events ?? []) as Parameters<typeof toUIEvent>[0][];
    return rows.slice(0, PER_SOURCE).map((dto): EntityHit => {
      const ev = toUIEvent(dto);
      return { key: "ev:" + ev.id, source: "events", title: ev.title, sub: [ev.date, ev.location && ev.location !== "—" ? ev.location : ""].filter(Boolean).join(" · "), open: { event: ev } };
    });
  }), (k) => deny("events", k));
  const customers = useRemoteSource(wantCustomers, scopeKey, gen, q, nonce, () => listBookingCustomers({ q: query.trim(), limit: PER_SOURCE }).then((r) => {
    const res = r as unknown as { data?: CustomerRow[]; customers?: CustomerRow[] };
    const rows = res.data ?? res.customers ?? [];
    return rows.slice(0, PER_SOURCE).map((c): EntityHit => ({
      key: "cu:" + c.id,
      source: "customers",
      title: (c.displayName || c.name || [c.firstName, c.lastName].filter(Boolean).join(" ") || c.email || c.id).trim(),
      sub: c.email || "",
      open: { route: "customers.detail", params: { customerId: c.id } },
    }));
  }), (k) => deny("customers", k));

  /* While a switch is pending: no records at all, and each source the role
     can search shows as loading until the new workspace is confirmed. */
  if (searching && pending) {
    return {
      hits: [],
      state: { calendar: scope.calendar ? "loading" : "idle", events: scope.events ? "loading" : "idle", customers: scope.customers ? "loading" : "idle" },
      calendarTruncated: false,
      retry: () => { forceCal.current = true; setNonce((n) => n + 1); },
    };
  }
  /* Only an answer read under the CURRENT workspace + user is ever shown. */
  const calCurrent = wantCal && cal.cache && cal.cache.key === scopeKey ? cal.cache : null;
  const calHits = calCurrent ? calendarHits(calCurrent, q) : [];
  const calState: SourceState = !wantCal ? "idle" : cal.state === "loading" && calCurrent ? "ready" : cal.state === "ready" && !calCurrent ? "loading" : cal.state;
  return {
    hits: calHits.concat(events.hits, customers.hits),
    state: {
      calendar: calState,
      events: events.state,
      customers: customers.state,
    },
    calendarTruncated: !!(calCurrent && calCurrent.truncated),
    retry: () => { forceCal.current = true; setNonce((n) => n + 1); },
  };
}

type CustomerRow = { id: string; displayName?: string; name?: string; firstName?: string; lastName?: string; email?: string };

/** The nouns the placeholder and label may honestly promise, in display order.
 *  Pages are always searchable (the role-filtered nav catalog). `short` is the
 *  visible placeholder: pages left out when records are searchable, "calendar"
 *  for "your calendar", at most two nouns — the field clips anything longer.
 *  The accessible name and the empty state use the full list. `labels` carries
 *  the tenant's own nav nouns (a
 *  university calls Customers "Students", Locations "Campuses"), so the field
 *  speaks the words the rail beside it uses. */
export function searchScopeNouns(scope: SearchScope, t: (k: string, f: string) => string, opts: { includePages?: boolean; short?: boolean; labels?: { customers?: string; locations?: string }; locale?: string } = {}): string[] {
  const out: string[] = [];
  /* Nav labels are title-case; mid-sentence English wants them lower-case, but
     a German noun must keep its capital, so only English is folded. */
  const fold = /^en(-|$)/i.test(opts.locale || "en");
  const lower = (s: string | undefined, fallback: string) => (s && s.trim() ? (fold ? s.trim().toLocaleLowerCase("en") : s.trim()) : fallback);
  if (scope.events) out.push(t("chrome.searchPanel.noun.events", "events"));
  else if (scope.calendar) out.push(opts.short ? t("chrome.searchPanel.noun.calendar", "calendar") : t("chrome.searchPanel.noun.yourCalendar", "your calendar"));
  if (scope.customers) out.push(lower(opts.labels?.customers, t("chrome.searchPanel.noun.customers", "customers")));
  if (scope.locations) out.push(lower(opts.labels?.locations, t("chrome.searchPanel.noun.locations", "locations")));
  if (opts.includePages || out.length === 0) out.push(t("chrome.searchPanel.noun.pages", "pages"));
  /* The visible field holds about 34 characters (226px of text at 14.5px —
     measured: "Search events, customers, locations…" is 249px and clips). Two
     nouns always fit; the rest are still searched and still named in the
     accessible name and the empty state. */
  return opts.short ? out.slice(0, 2) : out;
}

/** "events, customers, locations and pages", in the active locale's list form. */
export function joinNouns(nouns: string[], locale: string): string {
  try {
    return new Intl.ListFormat(locale || "en", { style: "long", type: "conjunction" }).format(nouns);
  } catch {
    return nouns.join(", ");
  }
}
